import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Modal,
  ModalDialog,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Alert,
  CircularProgress,
  DialogTitle,
  DialogActions,
  Checkbox,
  Select,
  Option,
} from '@mui/joy';
import { PencilSimple as Edit, Trash as Delete, UserPlus as PersonAdd, X as Close } from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import toast from 'react-hot-toast';

interface RbacRole {
  id: number;
  name: string;
  is_system: boolean;
}

interface User {
  role?: { id: number; name: string } | null;
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_active: boolean;
  is_email_verified: boolean;
  is_organization_admin: boolean;
  commission_rate: string;
  can_schedule_builds: boolean;
  target_knocks_per_week: number;
  mobile_phone: string;
}

interface UserFormData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password?: string;
  is_organization_admin: boolean;
  commission_rate: string;
  can_schedule_builds: boolean;
  target_knocks_per_week: number;
  mobile_phone: string;
}

const PAGE_SIZE = 20;

// Fetch a page of users
const fetchUsers = async (page: number): Promise<{ users: User[]; count: number }> => {
  const response = await api.get<any>(`/api/users/?page=${page}&page_size=${PAGE_SIZE}`);
  const data = response.data;
  // Handle both array and paginated responses
  if (Array.isArray(data)) {
    return { users: data as User[], count: data.length };
  } else if (data?.results && Array.isArray(data.results)) {
    return { users: data.results as User[], count: data.count ?? data.results.length };
  }
  return { users: [], count: 0 };
};

// Create user (and optionally assign an access role once created)
const createUser = async ({
  userData,
  roleId,
}: {
  userData: Omit<UserFormData, 'password'>;
  roleId: string | null;
}): Promise<User> => {
  let created: User;
  try {
    const response = await api.post<User>('/api/users/', userData);
    created = response.data;
  } catch (error: any) {
    if (error.responseData?.error?.includes('Usage limit exceeded')) {
      throw new Error(error.responseData.error);
    }
    // The username is auto-set from the email, so surface duplicate-username
    // validation errors in terms of the email address
    if (error.responseData?.username || error.responseData?.email) {
      throw new Error('A user with this email address already exists.');
    }
    throw new Error(error.responseData?.detail || 'Failed to create user');
  }
  if (roleId) {
    try {
      await api.post(`/api/roles/${roleId}/assign/`, { user_id: created.id });
    } catch {
      // The user exists at this point — warn instead of failing the mutation
      toast.error('User created, but assigning the role failed. Set it from the Access role column.');
    }
  }
  return created;
};

// Update user
const updateUser = async ({
  id,
  userData,
  previousIsAdmin,
}: {
  id: number;
  userData: Partial<UserFormData>;
  previousIsAdmin: boolean;
}): Promise<User> => {
  try {
    // The user serializer ignores is_organization_admin (mass-assignment
    // protection) — admin status changes go through the dedicated endpoint.
    const { is_organization_admin, ...profileData } = userData;
    const response = await api.patch<User>(`/api/users/${id}/`, profileData);
    if (typeof is_organization_admin === 'boolean' && is_organization_admin !== previousIsAdmin) {
      const adminResponse = await api.post<User>(`/api/users/${id}/set-admin/`, {
        is_organization_admin,
      });
      return adminResponse.data;
    }
    return response.data;
  } catch (error: any) {
    throw new Error(error.responseData?.error || error.responseData?.detail || 'Failed to update user');
  }
};

// Delete user
class DeleteUserError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

const deleteUser = async (id: number): Promise<void> => {
  try {
    await api.delete<void>(`/api/users/${id}/`);
  } catch (error: any) {
    // The API explains WHY (e.g. the person created invoices that must be
    // kept) and gives a code the UI can act on — don't flatten it away.
    throw new DeleteUserError(
      error.responseData?.detail || error.responseData?.error || 'Failed to delete user',
      error.responseData?.code
    );
  }
};

// Activate user
const activateUser = async (id: number): Promise<User> => {
  try {
    const response = await api.post<User>(`/api/users/${id}/activate/`, {});
    return response.data;
  } catch (error: any) {
    throw new Error(error.responseData?.detail || 'Failed to activate user');
  }
};

// Deactivate user
const deactivateUser = async (id: number): Promise<User> => {
  try {
    const response = await api.post<User>(`/api/users/${id}/deactivate/`, {});
    return response.data;
  } catch (error: any) {
    throw new Error(error.responseData?.detail || 'Failed to deactivate user');
  }
};

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);
  // access role picked in the Add User modal ('' = full access / no role)
  const [newUserRoleId, setNewUserRoleId] = useState<string>('');
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    is_organization_admin: false,
    commission_rate: '0.00',
    can_schedule_builds: false,
    target_knocks_per_week: 0,
    mobile_phone: '',
  });
  const [error, setError] = useState<string | null>(null);

  // Redirect if not organization admin
  useEffect(() => {
    if (currentUser && !currentUser.is_organization_admin) {
      navigate('/settings');
    }
  }, [currentUser, navigate]);

  // Fetch users (paginated)
  const [page, setPage] = useState(1);
  const { data: usersPage, isLoading, isError, refetch } = useQuery({
    queryKey: ['users', page],
    queryFn: () => fetchUsers(page),
  });
  const users = usersPage?.users ?? [];
  const totalCount = usersPage?.count ?? 0;

  // If a delete empties the current page, step back to the last real page
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (!isLoading && !isError && page > maxPage) {
      setPage(maxPage);
    }
  }, [isLoading, isError, totalCount, page]);

  // access roles for the assignment select
  const { data: rbacRoles = [] } = useQuery({
    queryKey: ['rbac-roles'],
    queryFn: async () => {
      const response = await api.get('/api/roles/');
      const data = response.data as { results?: RbacRole[] } | RbacRole[];
      return Array.isArray(data) ? data : (data.results || []);
    },
  });

  const assignRoleMutation = useMutation({
    mutationFn: async ({ userId, roleId }: { userId: number; roleId: string | null }) => {
      if (roleId) {
        return (await api.post(`/api/roles/${roleId}/assign/`, { user_id: userId })).data;
      }
      return (await api.post('/api/roles/unassign/', { user_id: userId })).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['rbac-roles'] });
      toast.success('Role updated');
    },
    onError: () => toast.error('Could not update role'),
  });

  // Called only for real picks: MUI also fires the role Select's onChange with
  // no event when a user's role isn't among the options yet (roles still
  // loading), which would otherwise save "Full access" over their role.
  const handleRoleChange = (userId: number, value: string | null) => {
    assignRoleMutation.mutate({ userId, roleId: value || null });
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsAddModalOpen(false);
      resetForm();
      setError(null);
      toast.success('User created successfully');
    },
    onError: (error: Error) => {
      setError(error.message);
      toast.error(error.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingUser(null);
      resetForm();
      setError(null);
      toast.success('User updated successfully');
    },
    onError: (error: Error) => {
      setError(error.message);
      toast.error(error.message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteConfirmUser(null);
      toast.success('User deleted successfully');
    },
    onError: (error: Error) => {
      const failed = error as DeleteUserError;
      const target = deleteConfirmUser;
      setDeleteConfirmUser(null);
      if (failed.code === 'user_has_records' && target) {
        // Deleting would destroy an audit trail; deactivating achieves what
        // the admin actually wants, so offer it rather than dead-ending.
        setError(null);
        if (window.confirm(`${failed.message}\n\nDeactivate ${target.first_name || target.email} now?`)) {
          deactivateMutation.mutate(target.id);
          return;
        }
        toast(failed.message, { duration: 8000 });
        return;
      }
      setError(failed.message);
      toast.error(failed.message);
    },
  });

  // Activate mutation
  const activateMutation = useMutation({
    mutationFn: activateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User activated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: deactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deactivated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      is_organization_admin: false,
      commission_rate: '0.00',
      can_schedule_builds: false,
      target_knocks_per_week: 0,
      mobile_phone: '',
    });
    setNewUserRoleId('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingUser) {
      // Don't send password if it's empty
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      updateMutation.mutate({
        id: editingUser.id,
        userData: updateData,
        previousIsAdmin: editingUser.is_organization_admin,
      });
    } else {
      // For new users, don't include password (will be set during activation).
      // The username field is hidden — it mirrors the email address.
      const { password, ...createData } = formData;
      createMutation.mutate({
        userData: { ...createData, username: createData.email },
        roleId: formData.is_organization_admin ? null : newUserRoleId || null,
      });
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      password: '',
      is_organization_admin: user.is_organization_admin,
      commission_rate: user.commission_rate,
      can_schedule_builds: user.can_schedule_builds,
      target_knocks_per_week: user.target_knocks_per_week || 0,
      mobile_phone: user.mobile_phone || '',
    });
    setError(null);
  };

  const handleDelete = (user: User) => {
    setDeleteConfirmUser(user);
    setError(null);
  };

  if (!currentUser?.is_organization_admin) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Users & permissions</Typography>
          {totalCount > 0 && (
            <Chip size="sm" variant="soft" color="neutral">
              {totalCount} total
            </Chip>
          )}
          <PageHelpButton />
        </Box>
        <Button
          startDecorator={<PersonAdd />}
          onClick={() => {
            resetForm();
            setIsAddModalOpen(true);
            setError(null);
          }}
        >
          Add User
        </Button>
      </Box>

      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>Add a login for a teammate and choose what they can access. Employment and onboarding are managed in Team management.</Typography>
      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          {isError ? <Alert color="danger" endDecorator={<Button size="sm" variant="soft" color="danger" onClick={() => refetch()}>Retry</Button>}>Could not load users.</Alert> : isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveTable rows={Array.isArray(users) ? users : []} getRowKey={user => user.id} columns={[
              { key: 'name', header: 'Name', mobileVariant: 'header', render: user => <Typography level="title-sm">{user.full_name || user.username}</Typography> },
              { key: 'email', header: 'Email', mobileVariant: 'body', render: user => <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>{user.email}</Typography> },
              { key: 'username', header: 'Username', render: user => <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>{user.username}</Typography> },
              { key: 'type', header: 'Account type', render: user => <Chip size="sm" color={user.is_organization_admin ? 'primary' : 'neutral'}>{user.is_organization_admin ? 'Admin' : 'Member'}</Chip> },
              { key: 'role', header: 'Access role', render: user => user.is_organization_admin ? 'All access' : <Select size="sm" aria-label={`Access role for ${user.full_name || user.username}`} value={user.role ? String(user.role.id) : ''} onChange={(event, value) => { if (event) handleRoleChange(user.id, value); }} placeholder="Full access">
                <Option value="">Full access (no role)</Option>{(rbacRoles || []).filter(role => !role.is_system).map(role => <Option key={role.id} value={String(role.id)}>{role.name}</Option>)}
              </Select> },
              { key: 'commission', header: 'Commission', render: user => `${user.commission_rate}%` },
              { key: 'status', header: 'Status', render: user => <Chip size="sm" color={user.is_active ? 'success' : 'warning'}>{user.is_active ? 'Active' : 'Invited'}</Chip> },
              { key: 'verified', header: 'Email verified', render: user => <Chip size="sm" variant="soft" color={user.is_email_verified ? 'success' : 'warning'}>{user.is_email_verified ? 'Verified' : 'Pending'}</Chip> },
            ]} rowActions={user => <Box sx={{ display: 'flex', gap: .5 }}>
              <IconButton size="sm" variant="plain" aria-label={`Edit ${user.full_name || user.username}`} onClick={() => handleEdit(user)}><Edit /></IconButton>
              <IconButton size="sm" variant="plain" color="danger" aria-label={`Delete ${user.full_name || user.username}`} onClick={() => handleDelete(user)} disabled={user.username === currentUser?.username}><Delete /></IconButton>
            </Box>} emptyState="No users found." />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!isLoading && !isError && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography level="body-sm" color="neutral">
            {totalCount > 0
              ? `Showing ${Math.min((page - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount} users`
              : 'No users'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              disabled={page >= Math.ceil(totalCount / PAGE_SIZE)}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={isAddModalOpen || !!editingUser}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingUser(null);
          resetForm();
          setError(null);
        }}
      >
        <ModalDialog sx={{ minWidth: { xs: 'auto', sm: 400 }, maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' }, maxHeight: '90vh', overflow: 'auto' }}>
          <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {error && (
                <Alert 
                  color="danger" 
                  variant="soft"
                  endDecorator={
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => setError(null)}
                    >
                      <Close />
                    </IconButton>
                  }
                >
                  {error}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl required sx={{ flex: 1 }}>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </FormControl>
                <FormControl required sx={{ flex: 1 }}>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </FormControl>
              </Box>

              {editingUser && (
                <FormControl required>
                  <FormLabel>Username</FormLabel>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  />
                </FormControl>
              )}

              <FormControl required>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </FormControl>

              {editingUser && (
                <FormControl>
                  <FormLabel>Password (leave blank to keep current)</FormLabel>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Mobile Phone</FormLabel>
                <Input
                  type="tel"
                  placeholder="e.g. +18325551234"
                  value={formData.mobile_phone}
                  onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value })}
                />
                <FormHelperText>
                  Click-to-call rings this phone first, then connects the customer.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Commission Rate (%)</FormLabel>
                <Input
                  type="number"
                  value={formData.commission_rate}
                  onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                  slotProps={{
                    input: {
                      min: 0,
                      max: 100,
                      step: 0.01,
                    },
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Target Knocks per Week</FormLabel>
                <Input
                  type="number"
                  value={formData.target_knocks_per_week}
                  onChange={(e) => setFormData({ ...formData, target_knocks_per_week: parseInt(e.target.value) || 0 })}
                  slotProps={{
                    input: {
                      min: 0,
                      max: 1000,
                      step: 1,
                    },
                  }}
                />
                <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  Set to 0 to disable knock tracking for this user
                </Typography>
              </FormControl>

              {!editingUser && (
                <FormControl>
                  <FormLabel>Access role</FormLabel>
                  <Select
                    value={formData.is_organization_admin ? '' : newUserRoleId}
                    onChange={(_e, value) => setNewUserRoleId(value ?? '')}
                    disabled={formData.is_organization_admin}
                  >
                    <Option value="">Full access (no role)</Option>
                    {(rbacRoles || []).filter((r) => !r.is_system).map((r) => (
                      <Option key={r.id} value={String(r.id)}>{r.name}</Option>
                    ))}
                  </Select>
                  {formData.is_organization_admin && (
                    <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                      Organization Admins always have full access
                    </Typography>
                  )}
                </FormControl>
              )}

              <FormControl>
                <Checkbox
                  label="Organization Admin"
                  checked={formData.is_organization_admin}
                  disabled={!!editingUser && editingUser.username === currentUser?.username}
                  onChange={(e) => {
                    const isAdmin = e.target.checked;
                    setFormData({
                      ...formData,
                      is_organization_admin: isAdmin,
                      // If setting as admin, automatically enable can_schedule_builds
                      can_schedule_builds: isAdmin ? true : formData.can_schedule_builds
                    });
                  }}
                />
                {!!editingUser && editingUser.username === currentUser?.username && (
                  <Typography level="body-xs" sx={{ ml: 3, mt: 0.5, color: 'text.secondary' }}>
                    You cannot change your own admin status
                  </Typography>
                )}
              </FormControl>

              <FormControl>
                <Checkbox
                  label="Can Schedule Builds"
                  checked={formData.can_schedule_builds}
                  disabled={formData.is_organization_admin}
                  onChange={(e) => setFormData({ ...formData, can_schedule_builds: e.target.checked })}
                  slotProps={{
                    root: {
                      sx: formData.is_organization_admin ? { opacity: 0.7 } : {}
                    }
                  }}
                />
                {formData.is_organization_admin && (
                  <Typography level="body-xs" sx={{ ml: 3, mt: 0.5, color: 'text.secondary' }}>
                    Organization Admins always have permission to schedule builds
                  </Typography>
                )}
              </FormControl>

              <DialogActions>
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setEditingUser(null);
                    resetForm();
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
                  {editingUser ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </Box>
          </form>
        </ModalDialog>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteConfirmUser}
        onClose={() => setDeleteConfirmUser(null)}
      >
        <ModalDialog sx={{ maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' } }}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <Typography>
            Are you sure you want to delete user "{deleteConfirmUser?.full_name || deleteConfirmUser?.username}"?
          </Typography>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={() => setDeleteConfirmUser(null)}>
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => deleteConfirmUser && deleteMutation.mutate(deleteConfirmUser.id)}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
